import {
  Button,
  SegmentedControl,
  styled,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useTaskSystemContext } from "../contexts";
import { useState } from "react";
import {
  ClockIcon,
  KanbanIcon,
  ListTodoIcon,
  Rows3Icon,
  SheetIcon,
} from "lucide-react";
import { TaskQuickFilter, TaskView } from "../enums";
import { usePermissions } from "@/hooks";

const StyledPageTitle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
  width: fit-content;
  padding: 2px 4px;
  font-weight: bold;
  font-size: 1rem;
`;

const ViewButton = styled(Button)<{ $active: boolean }>`
  font-weight: 525;

  &,
  & > * {
    opacity: ${({ $active }) => ($active ? 1 : 0.85)} !important;
    text-decoration: none !important;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

export const TaskHeader = () => {
  const theme = useTheme();
  const context = useTaskSystemContext();
  const { currentUser } = usePermissions();
  const [quickFilter, setQuickFilter] = useState<TaskQuickFilter>(
    TaskQuickFilter.ALL_TASKS
  );

  const handleQuickFilterChange = (value: TaskQuickFilter) => {
    setQuickFilter(value);

    switch (value) {
      case TaskQuickFilter.ALL_TASKS:
        context.setQuery((prev) => ({
          ...prev,
          user_id: undefined,
          assignee_id: undefined,
          created_by_id: undefined,
        }));
        break;
      case TaskQuickFilter.MY_TASKS:
        context.setQuery((prev) => ({
          ...prev,
          user_id: currentUser.user_id,
          assignee_id: undefined,
          created_by_id: undefined,
        }));
        break;
      case TaskQuickFilter.ASSIGNED_TO_ME:
        context.setQuery((prev) => ({
          ...prev,
          assignee_id: currentUser.user_id,
          user_id: undefined,
          created_by_id: undefined,
        }));
        break;
      case TaskQuickFilter.CREATED_BY_ME:
        context.setQuery((prev) => ({
          ...prev,
          created_by_id: currentUser.user_id,
          user_id: undefined,
          assignee_id: undefined,
        }));
        break;
      default:
        context.setQuery((prev) => ({
          ...prev,
          user_id: undefined,
          assignee_id: undefined,
          created_by_id: undefined,
        }));
        break;
    }
  };

  const handleViewChange = (value: TaskView) => {
    context.setTaskView(value);
    // localStorage.setItem("taskView", value);
  };

  const viewOptions = [
    { label: <KanbanIcon size={14} />, value: TaskView.KANBAN },
    { label: <Rows3Icon size={14} />, value: TaskView.LIST },
    { label: <SheetIcon size={14} />, value: TaskView.TABLE },
    { label: <ClockIcon size={14} />, value: TaskView.TIME },
  ];

  return (
    <HeaderContainer>
      <StyledPageTitle>
        <ListTodoIcon color={theme.palette.primary.main} size={18} />{" "}
        <span>Tasks</span>
      </StyledPageTitle>
      <ViewButton
        variant={
          quickFilter === TaskQuickFilter.ALL_TASKS ? "contained" : "outlined"
        }
        size="xxs"
        $active={quickFilter === TaskQuickFilter.ALL_TASKS}
        onClick={() => handleQuickFilterChange(TaskQuickFilter.ALL_TASKS)}
      >
        All Tasks
      </ViewButton>
      <ViewButton
        variant={
          quickFilter === TaskQuickFilter.MY_TASKS ? "contained" : "outlined"
        }
        size="xxs"
        $active={quickFilter === TaskQuickFilter.MY_TASKS}
        onClick={() => handleQuickFilterChange(TaskQuickFilter.MY_TASKS)}
      >
        My Tasks
      </ViewButton>
      <ViewButton
        variant={
          quickFilter === TaskQuickFilter.ASSIGNED_TO_ME
            ? "contained"
            : "outlined"
        }
        size="xxs"
        $active={quickFilter === TaskQuickFilter.ASSIGNED_TO_ME}
        onClick={() => handleQuickFilterChange(TaskQuickFilter.ASSIGNED_TO_ME)}
      >
        Assigned to Me
      </ViewButton>
      <ViewButton
        variant={
          quickFilter === TaskQuickFilter.CREATED_BY_ME
            ? "contained"
            : "outlined"
        }
        size="xxs"
        $active={quickFilter === TaskQuickFilter.CREATED_BY_ME}
        onClick={() => handleQuickFilterChange(TaskQuickFilter.CREATED_BY_ME)}
      >
        Created by Me
      </ViewButton>
      <div style={{ marginLeft: "auto" }}>
        <SegmentedControl
          data={viewOptions}
          variant="contained"
          activeColor="secondary"
          defaultValue={TaskView.KANBAN}
          size="xxs"
          onChange={(value) => handleViewChange(value as TaskView)}
        />
      </div>
    </HeaderContainer>
  );
};
